import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Pencil,
  Search,
  Shield,
  UserCheck,
  UserCog,
  UserPlus,
  UserX,
  Users,
  X,
} from "lucide-react";

import type { User } from "@/types";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { PASSWORD_MIN_LENGTH, firstPasswordIssue } from "@/lib/password";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  MetricTile,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const ACCOUNT_STATUSES = ["active", "inactive", "pending_verification"] as const;
const ALL_ROLES = ["buyer", "seller", "bl_advisor", "admin", "org_admin"] as const;
const PAGE_SIZE = 15;

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-500/15 text-emerald-600",
  inactive: "bg-slate-100 text-slate-600",
  pending_verification: "bg-amber-500/15 text-amber-600",
};

const ROLE_COLORS: Record<string, string> = {
  admin: "bg-rose-500/15 text-rose-600",
  org_admin: "bg-amber-500/15 text-amber-600",
  bl_advisor: "bg-violet-500/10 text-violet-600",
  seller: "bg-emerald-500/15 text-emerald-600",
  buyer: "bg-slate-100 text-slate-600",
};

const ROLE_LABELS: Record<string, string> = {
  admin: "admin",
  org_admin: "Org Admin",
  bl_advisor: "BL Advisor",
  seller: "seller",
  buyer: "buyer",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  pending_verification: "Pending Verification",
};

const PORTAL_ADMIN = "admin@portal.com";

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const isPortalAdmin = currentUser?.email === PORTAL_ADMIN;

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusTab, setStatusTab] = useState<"active" | "inactive">("active");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  // "Create admin" modal — any admin may provision another admin account.
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [caFirstName, setCaFirstName] = useState("");
  const [caLastName, setCaLastName] = useState("");
  const [caEmail, setCaEmail] = useState("");
  const [caPhone, setCaPhone] = useState("");
  const [caPassword, setCaPassword] = useState("");
  const [caShowPassword, setCaShowPassword] = useState(false);
  const [caLoading, setCaLoading] = useState(false);
  const toast = useToast();

  const closeCreateAdmin = () => {
    setShowCreateAdmin(false);
    setCaFirstName(""); setCaLastName(""); setCaEmail("");
    setCaPhone(""); setCaPassword(""); setCaShowPassword(false);
  };

  const handleCreateAdmin = async (event: React.FormEvent) => {
    event.preventDefault();
    const caIssue = firstPasswordIssue(caPassword);
    if (caIssue) {
      toast.error(caIssue);
      return;
    }
    setCaLoading(true);
    try {
      await api.post("/admin/create-admin", {
        email: caEmail.trim(),
        first_name: caFirstName.trim(),
        last_name: caLastName.trim(),
        password: caPassword,
        phone: caPhone.trim() || null,
      });
      toast.success(`Admin account created for ${caEmail.trim()}`);
      closeCreateAdmin();
      fetchUsers(1);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to create admin");
    } finally {
      setCaLoading(false);
    }
  };
  // Monotonic id per fetch: a slow response that has been superseded by a newer
  // request must not overwrite fresher rows (auto-search fires while typing).
  const requestSeq = useRef(0);
  // The search text the current result set reflects. The auto-search effect
  // compares against this so submits / Clear filters don't trigger a second,
  // redundant fetch for a query that is already applied.
  const appliedSearchRef = useRef("");

  useEffect(() => {
    fetchUsers(1);
    api.patch("/users/mark-users-seen", {}).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters are passed explicitly so a freshly-set search/tab takes effect
  // immediately, without waiting for the state update to flush into this closure.
  async function fetchUsers(
    targetPage: number = page,
    opts: { search?: string; status?: "active" | "inactive" } = {},
  ) {
    const effSearch = opts.search ?? searchQuery;
    const seq = ++requestSeq.current;
    appliedSearchRef.current = effSearch;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (effSearch) params.set("search", effSearch);
      params.set("status", opts.status ?? statusTab);
      params.set("skip", String((targetPage - 1) * PAGE_SIZE));
      params.set("limit", String(PAGE_SIZE));
      const data = await api.get<{ total_elements: number; data: User[] }>(`/admin/users?${params.toString()}`);
      if (seq !== requestSeq.current) return; // superseded by a newer fetch
      setUsers(data.data);
      setTotal(data.total_elements);
      setPage(targetPage);
    } catch {
      if (seq === requestSeq.current) toast.error("Failed to load users");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }

  // Auto-search: refetch shortly after the user stops typing, no Search click
  // needed. Skipped when the input already matches the applied search (mount,
  // explicit submit, Clear filters), which would otherwise double-fetch.
  useEffect(() => {
    if (searchQuery === appliedSearchRef.current) return;
    const timer = setTimeout(() => {
      if (searchQuery !== appliedSearchRef.current) fetchUsers(1, { search: searchQuery });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  async function changeStatus(userId: string, newStatus: string) {
    setActionLoading(userId);
    try {
      const updated = await api.patch<User>(`/admin/users/${userId}/status`, { status: newStatus });
      setUsers((prev) => prev.map((user) => (user.id === userId ? updated : user)));
      setEditingUser((prev) => (prev?.id === userId ? updated : prev));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to update status");
    } finally {
      setActionLoading(null);
    }
  }

  async function setRole(userId: string, role: string) {
    setActionLoading(userId);
    try {
      const updated = await api.post<User>(`/admin/users/${userId}/roles`, { role });
      setUsers((prev) => prev.map((user) => (user.id === userId ? updated : user)));
      setEditingUser((prev) => (prev?.id === userId ? updated : prev));
      toast.success(`Role changed to ${ROLE_LABELS[role] || role}`);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to update role");
    } finally {
      setActionLoading(null);
    }
  }

  async function toggleActivation(user: User) {
    const deactivating = user.status === "active";
    const newStatus = deactivating ? "inactive" : "active";
    const userName = `${user.first_name} ${user.last_name}`;
    if (deactivating && !confirm(`Deactivate ${userName}? They will no longer be able to log in.`)) return;

    setActionLoading(user.id);
    try {
      await api.patch(`/admin/users/${user.id}/status`, { status: newStatus });
      toast.success(deactivating ? `${userName} deactivated` : `${userName} activated`);
      // Re-fetch so the user moves to the correct tab.
      await fetchUsers(page);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to update status");
    } finally {
      setActionLoading(null);
    }
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    fetchUsers(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    fetchUsers(1, { search: "" });
  };

  const switchTab = (tab: "active" | "inactive") => {
    if (tab === statusTab) return;
    setStatusTab(tab);
    fetchUsers(1, { status: tab });
  };

  const hasFilters = Boolean(searchQuery);

  const metrics = useMemo(() => {
    const active = users.filter((user) => user.status === "active").length;
    const pending = users.filter((user) => user.status === "pending_verification").length;
    const admins = users.filter((user) => user.roles.includes("admin")).length;
    return { total, active, pending, admins };
  }, [users, total]);

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin Users"
        title="Keep account quality and role governance under control."
        description="This screen is rebuilt as a cleaner operations surface so status management, role assignment, and user cleanup are easier to scan and safer to execute."
        actions={
          <>
            <Button size="lg" className="rounded-full" onClick={() => setShowCreateAdmin(true)}>
              <UserPlus className="h-4 w-4" />
              Create admin
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white" onClick={() => fetchUsers(page)}>
              Refresh users
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Link to="/admin/home">
              <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white">
                Back to dashboard
              </Button>
            </Link>
          </>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">User mix</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{metrics.total} accounts</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {metrics.active} active, {metrics.pending} pending verification, and {metrics.admins} admin accounts currently present.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Active</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.active}</div>
              </div>
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Pending</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.pending}</div>
              </div>
            </div>
          </>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Total Users" value={metrics.total} icon={Users} tone="primary" />
        <MetricTile label="Active Accounts" value={metrics.active} icon={UserCheck} tone="emerald" />
        <MetricTile label="Pending Verification" value={metrics.pending} icon={Shield} tone="amber" />
        <MetricTile label="Admin Accounts" value={metrics.admins} icon={UserCog} tone="rose" />
      </section>

      <PagePanel>
        <PanelHeading
          title="Search and filter"
          description="Locate accounts by name or email — results update as you type."
        />

        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/55" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-10"
            />
          </div>
        </form>

        {hasFilters && (
          <div className="mt-3">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-destructive transition-colors duration-200 hover:bg-destructive/15"
            >
              <X className="h-3.5 w-3.5" />
              Clear filters
            </button>
          </div>
        )}
      </PagePanel>

      <PagePanel>
        <PanelHeading
          title="User records"
          description="Assign roles and manage account access. Deactivated accounts cannot log in."
        />

        <div className="mb-5 inline-flex rounded-full border border-border/60 bg-muted/40 p-1">
          {(["active", "inactive"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => switchTab(tab)}
              className={cn(
                "rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-150",
                statusTab === tab
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab === "active" ? "Active" : "Deactivated"}
            </button>
          ))}
        </div>

        {loading && users.length === 0 ? (
          <Loader className="min-h-[26vh]" />
        ) : users.length === 0 ? (
          <EmptyPanel
            icon={Users}
            title="No users found"
            description="Try adjusting your filters or search query to broaden the result set."
          />
        ) : (
          <div className={cn("overflow-x-auto rounded-2xl border border-border/60", loading && "pointer-events-none opacity-60")}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  <th className="px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">User</th>
                  <th className="px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">Roles</th>
                  <th className="px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">Joined</th>
                  <th className="px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">Email</th>
                  <th className="px-4 py-3 text-right text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40 bg-white">
                {users.map((user) => {
                  const isActing = actionLoading === user.id;

                  return (
                    <tr
                      key={user.id}
                      className={`cursor-pointer transition-all duration-150 hover:bg-primary/[0.04] hover:shadow-[inset_3px_0_0_hsl(41_58%_55%/0.35)] ${isActing ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      {/* User */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-black text-primary">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-foreground">{user.first_name} {user.last_name}</div>
                            <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Roles */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((role) => (
                            <span
                              key={role}
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em] ${ROLE_COLORS[role] || "bg-slate-100 text-slate-700"}`}
                            >
                              {ROLE_LABELS[role] || role}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Joined */}
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>

                      {/* Verified */}
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${user.email_verified ? "text-emerald-600" : "text-amber-600"}`}>
                          {user.email_verified ? "Verified" : "Unverified"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setViewingUser(user)} className="h-7 w-7 text-muted-foreground hover:text-primary" title="View user">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setEditingUser(user)} className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Edit user">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {user.status === "active" ? (
                            <Button variant="ghost" size="icon" onClick={() => toggleActivation(user)} className="h-7 w-7 text-destructive hover:bg-destructive/10" title="Deactivate user">
                              <UserX className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" onClick={() => toggleActivation(user)} className="h-7 w-7 text-emerald-600 hover:bg-emerald-500/15" title="Activate user">
                              <UserCheck className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalElements={total}
          pageSize={PAGE_SIZE}
          onChange={fetchUsers}
        />
      </PagePanel>

      {viewingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm" onClick={() => setViewingUser(null)}>
          <div className="my-auto w-full max-w-sm rounded-2xl border border-border bg-white p-5 shadow-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-border/60 pb-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-base font-black text-primary">
                {viewingUser.first_name?.[0]}{viewingUser.last_name?.[0]}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-foreground">{viewingUser.first_name} {viewingUser.last_name}</div>
                <div className="truncate text-sm text-muted-foreground">{viewingUser.email}</div>
              </div>
              <button onClick={() => setViewingUser(null)} className="ml-auto shrink-0 rounded-full p-1.5 hover:bg-muted">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <div className="mt-4 divide-y divide-border/40 text-sm">
              <div className="flex items-center justify-between py-2.5">
                <span className="text-muted-foreground">Status</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.12em] ${STATUS_COLORS[viewingUser.status] || "bg-slate-100 text-slate-600"}`}>
                  {STATUS_LABELS[viewingUser.status] || viewingUser.status}
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-muted-foreground">Roles</span>
                <div className="flex flex-wrap justify-end gap-1">
                  {viewingUser.roles.map((r) => (
                    <span key={r} className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.1em] ${ROLE_COLORS[r] || "bg-slate-100"}`}>{ROLE_LABELS[r] || r}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-muted-foreground">Joined</span>
                <span>{new Date(viewingUser.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-muted-foreground">Email verified</span>
                <span className={viewingUser.email_verified ? "font-semibold text-emerald-600" : "font-semibold text-amber-600"}>
                  {viewingUser.email_verified ? "Verified" : "Unverified"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm" onClick={() => setEditingUser(null)}>
          <div className="my-auto w-full max-w-md rounded-2xl border border-border bg-white p-5 shadow-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-border/60 pb-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-base font-black text-primary">
                {editingUser.first_name?.[0]}{editingUser.last_name?.[0]}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-foreground">{editingUser.first_name} {editingUser.last_name}</div>
                <div className="truncate text-sm text-muted-foreground">{editingUser.email}</div>
              </div>
              <button onClick={() => setEditingUser(null)} className="ml-auto shrink-0 rounded-full p-1.5 hover:bg-muted">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            <div className="mt-5">
              <div className="mb-2.5 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Account Status</div>
              <div className="flex flex-wrap gap-2">
                {ACCOUNT_STATUSES.map((s) => {
                  const isSelected = editingUser.status === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={isSelected || actionLoading === editingUser.id}
                      onClick={() => changeStatus(editingUser.id, s)}
                      className={cn(
                        "inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-150 disabled:cursor-default",
                        isSelected
                          ? `border-transparent ${STATUS_COLORS[s]} shadow-sm`
                          : "border-border/60 bg-card text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-foreground"
                      )}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-1 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Role</div>
              <p className="mb-2.5 text-[0.72rem] text-muted-foreground/70">Each user has exactly one role. Selecting a new role replaces the current one.</p>
              <div className="flex flex-wrap gap-2">
                {ALL_ROLES
                  /* Only show admin option to portal admin */
                  .filter((r) => r !== "admin" || isPortalAdmin)
                  .map((role) => {
                    const isSelected = editingUser.roles.includes(role);
                    return (
                      <button
                        key={role}
                        type="button"
                        disabled={isSelected || actionLoading === editingUser.id}
                        onClick={() => setRole(editingUser.id, role)}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-150 disabled:cursor-default",
                          isSelected
                            ? `border-transparent ${ROLE_COLORS[role] || "bg-slate-100 text-slate-700"} shadow-sm`
                            : "border-border/60 bg-card text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-foreground disabled:opacity-50"
                        )}
                      >
                        {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />}
                        {ROLE_LABELS[role] || role}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Admin modal ── */}
      {showCreateAdmin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !caLoading) closeCreateAdmin(); }}
        >
          <div className="w-full max-w-md rounded-2xl border border-border/80 bg-card shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/12 text-primary">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-extrabold text-foreground">Create admin account</p>
                  <p className="text-xs text-muted-foreground">Full administrative access — created active and verified.</p>
                </div>
              </div>
              {!caLoading && (
                <button onClick={closeCreateAdmin} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border hover:bg-muted text-muted-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <form onSubmit={handleCreateAdmin} className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">First name</label>
                  <Input value={caFirstName} onChange={(e) => setCaFirstName(e.target.value)} required placeholder="Jane" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Last name</label>
                  <Input value={caLastName} onChange={(e) => setCaLastName(e.target.value)} required placeholder="Doe" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Email</label>
                <Input type="email" value={caEmail} onChange={(e) => setCaEmail(e.target.value)} required placeholder="admin@company.com" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Phone <span className="font-normal normal-case tracking-normal">(optional)</span></label>
                <Input value={caPhone} onChange={(e) => setCaPhone(e.target.value)} placeholder="+1 (555) 000-0000" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Password</label>
                <div className="relative">
                  <Input
                    type={caShowPassword ? "text" : "password"}
                    value={caPassword}
                    onChange={(e) => setCaPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    placeholder={`Min. ${PASSWORD_MIN_LENGTH} characters`}
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setCaShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {caShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordRequirements password={caPassword} className="pt-1" />
              </div>

              <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 px-3.5 py-3 text-xs text-amber-600">
                <Shield className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <p>Admins can manage every user, listing, and payment on the platform. Only create accounts for people you trust.</p>
              </div>

              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={closeCreateAdmin} disabled={caLoading}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" disabled={caLoading}>
                  <UserPlus className="h-4 w-4" />
                  {caLoading ? "Creating..." : "Create admin"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  );
}
